import React, { useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, X, Plus, Trash2, Building, CreditCard } from 'lucide-react';
import {
  OFFICES, inr, todayKey, formatFinanceDate, shiftDate, isoToDateKey, daysBetween,
  netFee, balanceDue, DAILY_EXP_CATEGORIES, OFFICE_EXP_CATEGORIES,
} from './helpers';

// === Routing helpers — per the refined spec ===
// ADVANCE held by INSPECTOR (admin section if inspector is admin OR explicit advance_paid_to='Admin')
const inspectorIsAdmin = (r) => typeof r.inspected_by === 'string' && r.inspected_by.startsWith('admin-');
const advHasAmount = (r) => Number(r.advance || 0) > 0;

const advanceWithAdmin = (r) => advHasAmount(r) && (
  (r.advance_mode === 'Cash' && (inspectorIsAdmin(r) || r.advance_paid_to === 'Admin')) ||
  (r.advance_mode === 'UPI' && r.advance_upi_paid_to === 'Admin')
);
const advanceWithStaff = (r) => advHasAmount(r) && !advanceWithAdmin(r) && (
  (r.advance_mode === 'Cash' && !inspectorIsAdmin(r)) ||
  (r.advance_mode === 'UPI' && r.advance_upi_paid_to === 'Staff')
);
const advanceHolderId = (r) => (inspectorIsAdmin(r) ? r.staff_id : (r.inspected_by || r.staff_id));

// BALANCE — appears AS SOON AS Fee modal saves with pay_mode = Cash or UPI (NO longer waits for r.collected)
const balHasAmount = (r) => balanceDue(r) > 0 && (r.pay_mode === 'Cash' || r.pay_mode === 'UPI');
const balanceWithAdmin = (r) => balHasAmount(r) && (
  (r.pay_mode === 'Cash' && r.pay_cash_paid_to === 'Admin') ||
  (r.pay_mode === 'UPI' && r.pay_upi_paid_to === 'Admin')
);
const balanceWithStaff = (r) => balHasAmount(r) && !balanceWithAdmin(r) && (
  (r.pay_mode === 'Cash' && r.pay_cash_paid_to !== 'Admin') ||
  (r.pay_mode === 'UPI' && r.pay_upi_paid_to === 'Staff')
);

export default function FinanceTab({ reports, staff, dailyExpenses, officeExpenses, onUpdateReport, onAddDailyExpense, onVerifyDailyExpense, onDeleteDailyExpense, onAddOfficeExpense, onVerifyOfficeExpense, onDeleteOfficeExpense }) {
  const [office, setOffice] = useState('Karaikudi');
  const [sub, setSub] = useState('cashupi');
  const [date, setDate] = useState(todayKey());
  const [expStaff, setExpStaff] = useState('');
  const [expCat, setExpCat] = useState('Petrol');
  const [expAmt, setExpAmt] = useState('');
  const [expNote, setExpNote] = useState('');
  const [offCat, setOffCat] = useState('Rent');
  const [offAmt, setOffAmt] = useState('');
  const [offNote, setOffNote] = useState('');

  const isToday = date === todayKey();
  const officeStaff = staff.filter((s) => s.role === 'staff' && s.office === office);
  const officeReports = reports.filter((r) => r.office === office && !r.archived);

  const itemDate = (r, kind) => isoToDateKey(kind === 'advance' ? (r.advance_collection_date || r.created_at) : (r.collected_date || r.created_at)) || isoToDateKey(r.created_at);
  const pendingDays = (r, kind) => { const dk = itemDate(r, kind); return dk ? Math.max(0, daysBetween(dk, date)) : 0; };

  // ===== STAFF SETTLEMENT (cash/UPI held by staff) — admin section grouped by name =====
  const showAdvOnDate = (r) => {
    if (!r.advance_confirmed) return itemDate(r, 'advance') <= date;
    return isoToDateKey(r.advance_handover_date) === date;
  };
  const showBalOnDate = (r) => {
    if (!r.pay_confirmed) return itemDate(r, 'balance') <= date;
    return isoToDateKey(r.pay_received_date || r.collected_date) === date;
  };

  // Staff settlement — ALWAYS show every staff (even empty)
  const settlement = officeStaff.map((s) => {
    const advItems = officeReports
      .filter((r) => advanceWithStaff(r) && advanceHolderId(r) === s.user_id && showAdvOnDate(r))
      .map((r) => ({ r, kind: 'advance', amount: Number(r.advance), days: pendingDays(r, 'advance'), verified: !!r.advance_confirmed }));
    const balItems = officeReports
      .filter((r) => balanceWithStaff(r) && r.staff_id === s.user_id && showBalOnDate(r))
      .map((r) => ({ r, kind: 'balance', amount: balanceDue(r), days: pendingDays(r, 'balance'), verified: !!r.pay_confirmed }));
    const items = [...advItems, ...balItems];
    // Net Handover = received items minus verified expenses
    const received = items.filter((it) => it.verified).reduce((a, b) => a + b.amount, 0);
    const verifiedExp = dailyExpenses
      .filter((e) => e.staff_id === s.user_id && e.verified && isoToDateKey(e.verified_date) === date)
      .reduce((a, e) => a + Number(e.amount || 0), 0);
    const netHandover = Math.max(0, received - verifiedExp);
    return { staff: s, items, netHandover, received, verifiedExp };
  });

  // Admin section — grouped by admin name (advance_admin_name OR pay_admin_name OR inspector name fallback)
  const adminGroups = {};
  const pushAdmin = (key, label, payload) => {
    if (!key) return;
    adminGroups[key] = adminGroups[key] || { name: label, items: [] };
    adminGroups[key].items.push(payload);
  };
  officeReports.forEach((r) => {
    if (advanceWithAdmin(r) && showAdvOnDate(r)) {
      const nm = r.advance_admin_name || r.admin_name
        || (inspectorIsAdmin(r) ? (staff.find((u) => u.user_id === r.inspected_by) || {}).name : null)
        || 'Admin';
      pushAdmin(nm, nm, { r, kind: 'advance', amount: Number(r.advance), verified: !!r.advance_confirmed });
    }
    if (balanceWithAdmin(r) && showBalOnDate(r)) {
      const nm = r.pay_admin_name || r.admin_name || 'Admin';
      pushAdmin(nm, nm, { r, kind: 'balance', amount: balanceDue(r), verified: !!r.pay_confirmed });
    }
  });

  const confirmItem = (r, kind) => {
    if (kind === 'advance') onUpdateReport(r.id, { advance_confirmed: true, advance_handover_date: new Date().toISOString() });
    else onUpdateReport(r.id, { pay_confirmed: true, pay_received_date: new Date().toISOString() });
  };
  const unconfirmItem = (r, kind) => {
    if (kind === 'advance') onUpdateReport(r.id, { advance_confirmed: false, advance_handover_date: null });
    else onUpdateReport(r.id, { pay_confirmed: false, pay_received_date: null });
  };

  // ===== EXPENSES on selected date =====
  const dayDaily = dailyExpenses.filter((e) => officeStaff.some((s) => s.user_id === e.staff_id) && e.date === date);
  const dayOffice = officeExpenses.filter((e) => e.office === office && e.date === date);

  // Format a date key (YYYY-MM-DD) as a short "DD-MMM" label
  const shortDate = (key) => {
    if (!key) return '';
    const [y, m, d] = key.split('-').map(Number);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d}-${months[m - 1] || ''}`;
  };
  const itemDateLabel = (r, kind) => shortDate(itemDate(r, kind));
  const carryDaily = dailyExpenses.filter((e) => officeStaff.some((s) => s.user_id === e.staff_id) && !e.verified && e.date < date);
  const carryOffice = officeExpenses.filter((e) => e.office === office && !e.verified && e.date < date);

  // ===== FINAL BALANCE (verified, on date) =====
  // Today's verified cash & UPI received (advance + balance), correctly using the verification dates
  const advVerifiedToday = officeReports.filter((r) => r.advance_confirmed && isoToDateKey(r.advance_handover_date) === date);
  const balVerifiedToday = officeReports.filter((r) => r.pay_confirmed && isoToDateKey(r.pay_received_date || r.collected_date) === date);
  const cashRcvToday = advVerifiedToday.filter((r) => r.advance_mode === 'Cash').reduce((a, r) => a + Number(r.advance || 0), 0)
    + balVerifiedToday.filter((r) => r.pay_mode === 'Cash').reduce((a, r) => a + balanceDue(r), 0);
  const upiRcvToday = advVerifiedToday.filter((r) => r.advance_mode === 'UPI').reduce((a, r) => a + Number(r.advance || 0), 0)
    + balVerifiedToday.filter((r) => r.pay_mode === 'UPI').reduce((a, r) => a + balanceDue(r), 0);
  const verifiedDailyExp = dayDaily.filter((e) => e.verified).reduce((a, e) => a + Number(e.amount || 0), 0);
  const verifiedOfficeExp = dayOffice.filter((e) => e.verified).reduce((a, e) => a + Number(e.amount || 0), 0);
  const netCashDay = cashRcvToday - verifiedDailyExp - verifiedOfficeExp;
  const netUpiDay = upiRcvToday;

  const addDaily = () => {
    if (!expStaff || !Number(expAmt)) return;
    if (expCat === 'Other' && !expNote.trim()) { toast.error('Please describe the "Other" expense'); return; }
    onAddDailyExpense({ staff_id: expStaff, date, category: expCat, amount: Number(expAmt), note: expNote.trim() || null });
    setExpAmt(''); setExpNote('');
  };
  const addOffice = () => {
    if (!Number(offAmt)) return;
    if (offCat === 'Other' && !offNote.trim()) { toast.error('Please describe the "Other" expense'); return; }
    onAddOfficeExpense({ date, office, category: offCat, amount: Number(offAmt), note: offNote.trim() || null });
    setOffAmt(''); setOffNote('');
  };

  // ===== BANK CREDIT pane =====
  const bankWorks = officeReports.filter((r) => r.pay_mode === 'Bank Credit' && r.report_done && r.collected);
  const banks = {};
  bankWorks.forEach((r) => { const b = r.bank || 'Unknown Bank'; (banks[b] = banks[b] || []).push(r); });
  const tierColor = (d) => (d >= 30 ? { bg: '#fef2f2', bd: '#fca5a5', tx: '#b91c1c' } : d >= 15 ? { bg: '#fffbeb', bd: '#fde68a', tx: '#92400e' } : { bg: '#f0fdf4', bd: '#86efac', tx: '#15803d' });
  const totalPending = bankWorks.filter((r) => !r.pay_confirmed).reduce((a, r) => a + balanceDue(r), 0);

  const card = { background: '#fff', border: '1px solid #e7e5e4', borderRadius: 8, padding: 12, marginBottom: 12 };
  const h3 = { fontFamily: 'var(--font-title)', fontSize: 14, fontWeight: 700, marginBottom: 8 };

  return (
    <div data-testid="finance-tab">
      <div className="fin-office-tabs">{OFFICES.map((o) => <button key={o} className={`fin-office-tab${office === o ? ' active' : ''}`} onClick={() => setOffice(o)} data-testid={`fin-office-${o}`}>{o}</button>)}</div>

      <div className="status-tabs" style={{ maxWidth: 360 }}>
        <button className={`status-tab${sub === 'cashupi' ? ' active' : ''}`} onClick={() => setSub('cashupi')} data-testid="fin-sub-cashupi">Cash/UPI Fee</button>
        <button className={`status-tab${sub === 'bankcredit' ? ' active' : ''}`} onClick={() => setSub('bankcredit')} data-testid="fin-sub-bank">Bank Credit Fee</button>
      </div>

      {sub === 'cashupi' && (
        <>
          {/* Date nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14, background: '#fff', border: '1px solid #e7e5e4', borderRadius: 8, padding: '8px 12px' }}>
            <button className="btn-outline" style={{ padding: '6px 8px' }} onClick={() => setDate(shiftDate(date, -1))} data-testid="date-prev"><ChevronLeft className="icon" /></button>
            <div style={{ textAlign: 'center' }}>
              <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 100, background: isToday ? '#1c1917' : '#f5f5f4', color: isToday ? '#fef3c7' : '#78716c', marginBottom: 2 }}>{isToday ? 'TODAY' : 'PAST'}</span>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{formatFinanceDate(date)}</div>
              {!isToday && <button onClick={() => setDate(todayKey())} style={{ background: 'none', border: 'none', color: '#1d4ed8', cursor: 'pointer', fontSize: 11, textDecoration: 'underline', fontFamily: 'inherit' }} data-testid="jump-today">Jump to Today</button>}
            </div>
            <button className="btn-outline" style={{ padding: '6px 8px' }} disabled={isToday} onClick={() => setDate(shiftDate(date, 1))} data-testid="date-next"><ChevronRight className="icon" /></button>
          </div>

          {/* === Cash/UPI Pending Settlement === */}
          <div style={card}>
            <div style={h3}>Cash/UPI Pending Settlement</div>

            {/* ADMIN SECTION — always visible */}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#6d28d9', marginBottom: 6, letterSpacing: '0.5px' }}>ADMIN SECTION</div>
            {Object.keys(adminGroups).length === 0 ? (
              <div data-testid="admin-empty"
                style={{ border: '1.5px dashed #86efac', borderRadius: 6, padding: 14, background: '#f0fdf4',
                  textAlign: 'center', fontSize: 12, color: '#15803d', fontStyle: 'italic', marginBottom: 12 }}>
                ✓ All clear — no admin pending items
              </div>
            ) : (
              Object.entries(adminGroups).map(([key, grp]) => (
                <div key={key} style={{ border: '1px solid #e9d5ff', borderRadius: 6, padding: 10, marginBottom: 8, background: '#faf5ff' }}
                  data-testid={`admin-group-${key.replace(/\s+/g, '-')}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 13 }}>{grp.name}</strong>
                    <span style={{ fontSize: 9, fontWeight: 800, color: '#6d28d9', background: '#ede9fe', padding: '2px 8px', borderRadius: 100, letterSpacing: '0.5px' }}>ADMIN</span>
                  </div>
                  {grp.items.map(({ r, kind, amount, verified }) => {
                    const itDate = isoToDateKey(kind === 'advance' ? r.advance_collection_date : (r.collected_date || r.updated_at || r.created_at));
                    const days = itDate ? Math.max(0, daysBetween(itDate, date)) : 0;
                    return (
                      <div key={r.id + kind} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                        background: verified ? '#f0fdf4' : (days > 0 ? '#fffbeb' : '#fff'),
                        border: `1px solid ${verified ? '#86efac' : (days > 0 ? '#fde68a' : '#e7e5e4')}`,
                        borderRadius: 5, padding: '6px 8px', marginBottom: 4 }}>
                        <div style={{ fontSize: 12, minWidth: 0 }}>
                          <span style={{ fontWeight: 600 }}>{r.client_name}</span>
                          <div style={{ fontSize: 9, color: '#78716c', marginTop: 1 }}>
                            <span style={{ background: kind === 'advance' ? '#dbeafe' : '#fef3c7', color: kind === 'advance' ? '#1d4ed8' : '#92400e', padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase', fontWeight: 700, marginRight: 4 }}>{kind}</span>
                            <span style={{ background: '#f5f5f4', padding: '1px 6px', borderRadius: 3, fontWeight: 700, marginRight: 4 }}>{r.pay_mode === 'Bank Credit' ? '—' : (kind === 'advance' ? r.advance_mode : r.pay_mode)}</span>
                            <span style={{ color: '#44403c', fontWeight: 700, marginRight: 4 }}>{itemDateLabel(r, kind)}</span>
                            {!verified && days > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>{days}d pending</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className="mono" style={{ fontWeight: 700, fontSize: 12 }}>{inr(amount)}</span>
                          {verified
                            ? <button className="confirm-badge confirmed-yes" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => unconfirmItem(r, kind)} data-testid={`verified-${kind}-${r.id}`}><Check className="icon" /> Verified</button>
                            : <button className="confirm-badge confirmed-no" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => confirmItem(r, kind)} data-testid={`verify-${kind}-${r.id}`}><X className="icon" /> Not Verified</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}

            {/* STAFF SECTION — always show every staff card */}
            <div style={{ fontSize: 11, fontWeight: 800, color: '#1c1917', marginTop: 14, marginBottom: 6, letterSpacing: '0.5px' }}>STAFF SECTION</div>
            {settlement.map(({ staff: s, items, netHandover }) => (
              <div key={s.user_id} style={{ border: '1px solid #e7e5e4', borderRadius: 6, padding: 10, marginBottom: 8, background: items.length > 0 ? '#fffbeb' : '#fff' }}
                data-testid={`settle-staff-${s.user_id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div>
                    <strong style={{ fontSize: 13 }}>{s.name}</strong>
                    <div style={{ fontSize: 10, color: '#78716c' }}>{s.office} Office</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 9, color: '#78716c', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Net Handover</div>
                    <span className="mono" style={{ fontWeight: 800, fontSize: 13, color: netHandover > 0 ? '#92400e' : '#15803d' }}>{inr(netHandover)}</span>
                  </div>
                </div>
                {items.length === 0 ? (
                  <div data-testid={`staff-empty-${s.user_id}`}
                    style={{ border: '1.5px dashed #86efac', borderRadius: 6, padding: 10, background: '#f0fdf4',
                      textAlign: 'center', fontSize: 11, color: '#15803d', fontStyle: 'italic' }}>
                    ✓ All clear — no pending items
                  </div>
                ) : items.map(({ r, kind, amount, days, verified }) => (
                  <div key={r.id + kind} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                    background: verified ? '#f0fdf4' : (days > 0 ? '#fffbeb' : '#fff'),
                    border: `1px solid ${verified ? '#86efac' : (days > 0 ? '#fde68a' : '#e7e5e4')}`,
                    borderRadius: 5, padding: '6px 8px', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>{r.client_name}</span>
                      <div style={{ fontSize: 9, color: '#78716c', marginTop: 1 }}>
                        <span style={{ background: kind === 'advance' ? '#dbeafe' : '#fef3c7', color: kind === 'advance' ? '#1d4ed8' : '#92400e', padding: '1px 6px', borderRadius: 3, textTransform: 'uppercase', fontWeight: 700, marginRight: 4 }}>{kind}</span>
                        <span style={{ background: '#f5f5f4', padding: '1px 6px', borderRadius: 3, fontWeight: 700, marginRight: 4 }}>{kind === 'advance' ? r.advance_mode : r.pay_mode}</span>
                        <span style={{ color: '#44403c', fontWeight: 700, marginRight: 4 }}>{itemDateLabel(r, kind)}</span>
                        {!verified && days > 0 && <span style={{ color: '#b91c1c', fontWeight: 700 }}>{days}d pending</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="mono" style={{ fontWeight: 700, fontSize: 12 }}>{inr(amount)}</span>
                      {verified
                        ? <button className="confirm-badge confirmed-yes" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => unconfirmItem(r, kind)} data-testid={`received-${kind}-${r.id}`}><Check className="icon" /> Received</button>
                        : <button className="confirm-badge confirmed-no" style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => confirmItem(r, kind)} data-testid={`confirm-${kind}-${r.id}`}><X className="icon" /> Not Received</button>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Daily expenses — box-style per staff */}
          <div style={card}>
            <div style={h3}>Daily Field Expenses</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              <select className="filter-select" value={expStaff} onChange={(e) => setExpStaff(e.target.value)} data-testid="exp-staff"><option value="">Staff…</option>{officeStaff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}</select>
              <select className="filter-select" value={expCat} onChange={(e) => setExpCat(e.target.value)} data-testid="exp-cat">{DAILY_EXP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <input className="filter-select" style={{ width: 80, fontFamily: 'var(--font-mono)' }} type="number" placeholder="₹" value={expAmt} onChange={(e) => setExpAmt(e.target.value)} data-testid="exp-amt" />
              <button className="btn-primary" style={{ padding: '6px 10px' }} onClick={addDaily} data-testid="add-daily-exp"><Plus className="icon" /></button>
            </div>
            {expCat === 'Other' && (
              <input className="field-input" style={{ marginBottom: 10, fontSize: 13 }}
                placeholder="What is this expense for? (required)"
                value={expNote} onChange={(e) => setExpNote(e.target.value)}
                data-testid="exp-other-note" />
            )}
            {[...carryDaily, ...dayDaily].length === 0 && <div style={{ fontSize: 12, color: '#78716c', textAlign: 'center', padding: 6 }}>No expenses.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {officeStaff.map((s) => {
                const list = [...carryDaily, ...dayDaily].filter((e) => e.staff_id === s.user_id);
                if (list.length === 0) return null;
                return (
                  <div key={s.user_id} data-testid={`daily-staff-${s.user_id}`}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#44403c', marginBottom: 5 }}>{s.name}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
                      {list.map((e) => (
                        <ExpenseBox key={e.id} e={e} carry={e.date < date}
                          onVerify={() => onVerifyDailyExpense(e.id, !e.verified)}
                          onDelete={() => onDeleteDailyExpense(e.id)} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Office expenses — box-style cards */}
          <div style={card}>
            <div style={h3}>Office Expenses</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
              <select className="filter-select" value={offCat} onChange={(e) => setOffCat(e.target.value)} data-testid="off-cat">{OFFICE_EXP_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <input className="filter-select" style={{ width: 80, fontFamily: 'var(--font-mono)' }} type="number" placeholder="₹" value={offAmt} onChange={(e) => setOffAmt(e.target.value)} data-testid="off-amt" />
              <button className="btn-primary" style={{ padding: '6px 10px' }} onClick={addOffice} data-testid="add-office-exp"><Plus className="icon" /></button>
            </div>
            {offCat === 'Other' && (
              <input className="field-input" style={{ marginBottom: 10, fontSize: 13 }}
                placeholder="What is this expense for? (required)"
                value={offNote} onChange={(e) => setOffNote(e.target.value)}
                data-testid="off-other-note" />
            )}
            {[...carryOffice, ...dayOffice].length === 0 && <div style={{ fontSize: 12, color: '#78716c', textAlign: 'center', padding: 6 }}>No office expenses.</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 6 }}>
              {[...carryOffice, ...dayOffice].map((e) => (
                <ExpenseBox key={e.id} e={e} carry={e.date < date}
                  onVerify={() => onVerifyOfficeExpense(e.id, !e.verified)}
                  onDelete={() => onDeleteOfficeExpense(e.id)} />
              ))}
            </div>
          </div>

          {/* Final balance */}
          <div style={{ ...card, background: 'linear-gradient(135deg,#2B2A28,#44403c)', color: '#fff' }} data-testid="final-balance">
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, opacity: 0.7, marginBottom: 8 }}>{office} Office Net — {isToday ? 'Today' : formatFinanceDate(date)}</div>
            <BalRow label="Cash received (verified)" value={inr(cashRcvToday)} />
            <BalRow label="Less: Daily expenses (verified)" value={'-' + inr(verifiedDailyExp)} />
            <BalRow label="Less: Office expenses (verified)" value={'-' + inr(verifiedOfficeExp)} />
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.2)', marginTop: 6, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
              <span>Net Cash</span><span className="mono" style={{ color: netCashDay >= 0 ? '#86efac' : '#fca5a5' }} data-testid="net-cash">{inr(netCashDay)}</span>
            </div>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', marginTop: 10, paddingTop: 8 }}>
              <BalRow label="UPI received (verified)" value={inr(upiRcvToday)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, marginTop: 4 }}>
                <span>Net UPI</span><span className="mono" style={{ color: '#86efac' }} data-testid="net-upi">{inr(netUpiDay)}</span>
              </div>
            </div>
          </div>
        </>
      )}

      {sub === 'bankcredit' && (
        <>
          <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#78716c', textTransform: 'uppercase', letterSpacing: 1 }}>Total Pending from Banks</div>
            <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: '#b45309' }}>{inr(totalPending)}</div>
          </div>
          {Object.keys(banks).length === 0 && <div className="empty"><h3>No bank fees pending</h3><p>Works appear here when Pay Mode is Bank Credit, the report is completed, and submitted to the bank.</p></div>}
          {Object.entries(banks).map(([bank, list]) => {
            const notCredited = list.filter((r) => !r.pay_confirmed);
            const credited = list.filter((r) => r.pay_confirmed);
            const oldest = Math.max(0, ...notCredited.map((r) => daysBetween(isoToDateKey(r.collected_date || r.created_at), todayKey())));
            return (
              <div key={bank} style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}><Building className="icon" /> {bank}</div>
                  <div style={{ fontSize: 11, color: '#78716c' }}>{notCredited.length} not credited &middot; oldest {oldest}d &middot; {credited.length} credited</div>
                </div>
                {list.map((r) => {
                  const d = daysBetween(isoToDateKey(r.collected_date || r.created_at), todayKey());
                  const tier = tierColor(d);
                  return (
                    <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, background: r.pay_confirmed ? '#f0fdf4' : tier.bg, border: `1px solid ${r.pay_confirmed ? '#86efac' : tier.bd}`, borderRadius: 6, padding: '8px 10px', marginBottom: 5 }} data-testid={`bank-row-${r.id}`}>
                      <div style={{ fontSize: 12, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{r.client_name}</div>
                        <div style={{ fontSize: 10, color: '#78716c' }}>{r.file_ref || '—'}{r.branch ? ' · ' + r.branch : ''} · {d}d</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span className="mono" style={{ fontWeight: 700, fontSize: 13 }}>{inr(balanceDue(r) || netFee(r))}</span>
                        <button className={`confirm-badge ${r.pay_confirmed ? 'confirmed-yes' : 'confirmed-no'}`} style={{ fontSize: 10, padding: '4px 8px' }} onClick={() => onUpdateReport(r.id, { pay_confirmed: !r.pay_confirmed, pay_received_date: !r.pay_confirmed ? new Date().toISOString() : null })} data-testid={`bank-toggle-${r.id}`}>
                          {r.pay_confirmed ? <><Check className="icon" /> Credited</> : <><X className="icon" /> Not Credited</>}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const BalRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3, opacity: 0.92 }}><span>{label}</span><span className="mono">{value}</span></div>
);

function ExpenseBox({ e, carry, onVerify, onDelete }) {
  const verified = !!e.verified;
  const bg = verified ? '#f0fdf4' : carry ? '#fffbeb' : '#fafaf9';
  const border = verified ? '#86efac' : carry ? '#fde68a' : '#e7e5e4';
  // If category is "Other" and a note was filled in, show the note as the title
  const title = (e.category === 'Other' && e.note && e.note.trim()) ? e.note : e.category;
  return (
    <div style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: 6, padding: '8px 10px',
      display: 'flex', flexDirection: 'column', gap: 4, minHeight: 76 }} data-testid={`exp-box-${e.id}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1c1917' }}>{title}</span>
        <button onClick={onDelete} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a8a29e', padding: 0, lineHeight: 1 }} data-testid={`del-exp-${e.id}`}><Trash2 style={{ width: 12, height: 12 }} /></button>
      </div>
      <div className="mono" style={{ fontSize: 14, fontWeight: 800, color: '#1c1917' }}>{inr(e.amount)}</div>
      {carry && !verified && (
        <div style={{ fontSize: 9, color: '#b91c1c', fontWeight: 700 }}>{e.date} pending</div>
      )}
      <button className={`confirm-badge ${verified ? 'confirmed-yes' : 'confirmed-no'}`}
        style={{ fontSize: 10, padding: '3px 7px', marginTop: 'auto' }}
        onClick={onVerify} data-testid={`verify-exp-${e.id}`}>
        {verified ? <><Check className="icon" /> Verified</> : <><X className="icon" /> Not Verified</>}
      </button>
    </div>
  );
}
